/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
import * as THREE from 'three';
// import { MathUtils } from 'three';
import { gsap } from "gsap";
import { MATERIAL_THICKNESS as MT, BLEED } from '@/box/constants.js'
import { CLIPPING_FIX_OFFSET } from './constants.js'
import { getRandomThreeJSColor } from '@/utils/common';

const HALF_MT = MT / 2;

/**
 * Calculates the width, height, and depth of a 3D object in Three.js.
 *
 * @param {THREE.Object3D} object - The Three.js object to measure.
 * @return {Object} An object containing the width, height, and depth of the input object.
 */
function getObjectDimensions(object) {
  const boundingBox = new THREE.Box3().setFromObject(object);

  const width = boundingBox.max.x - boundingBox.min.x;
  const height = boundingBox.max.y - boundingBox.min.y;
  const depth = boundingBox.max.z - boundingBox.min.z;

  return {
      width: width,
      height: height,
      depth: depth
  };
}

/**
 * Calculates the center point of the given object's bounding box.
 *
 * @param {THREE.Object3D} object - The object for which to calculate the center.
 * @returns {THREE.Vector3} The center point of the object's bounding box.
 */
function getObjectCenter(object) {
  const boundingBox = new THREE.Box3().setFromObject(object);
  const center = boundingBox.getCenter(new THREE.Vector3());

  return center;
}

function putDebugCube(parent, position) {
  const cubeGeo = new THREE.SphereGeometry(0.5, 4, 2);
  const cubeMat = new THREE.MeshStandardMaterial({
    wireframe: true,
    color: 0xFF0000,
  })
  const cubeMesh = new THREE.Mesh(cubeGeo, cubeMat);

  cubeMesh.position.copy(position);
  parent.add(cubeMesh);
}

function setPanelMaterials(group) {
  group.children.forEach(async (p) => {
    // if panelName is not defined or empty, skip
    if (!p.panelName) return;

    p.outsideMatIndex = 0
  });
}

// deg to rad
function deg2Rad(deg) {
  return deg * Math.PI / 180;
}

function foldPanels(box, group) {

  const {
    width: boxWidth,
    length: boxLength,
    depth: boxDepth
  } = box;

  group.children.forEach(async (p) => {

    // get panel dimensions (width, height, depth)
    let dim = getObjectDimensions(p)


    const skip = [
      // 'outsideFrontDustFlapLeft',
      // 'outsideFrontWall',
      // 'outsideFrontDustFlapRight',
      // 'insideLeftWall',
      // 'leftOverlap',
      // 'outsideLeftWall',
      // 'outsideBottom',
      // 'outsideRightWall',
      // 'rightOverlap',
      // 'insideRightWall',
      // 'outsideBackWallDustFlapLeft',
      // 'outsideBackWall',
      // 'outsideBackWallDustFlapRight',
      // 'leftDustFlapOutside',
      // 'topSide',
      // 'rightDustFlapOutside',
      // 'leftClosingFlap',
      // 'frontFlapOutside',
      // 'rightClosingFlap',
    ]
    // const pName = p.panelName.split(' ')[0]
    // if (skip.includes(pName)) return;

    // row 1

    if (p.panelName.includes('frontLeftFlap')) {

      // fold 1
      p.position.z += dim.depth / 2 + MT;
      p.position.y -= dim.depth / 2;
      p.rotation.x = -Math.PI / 2;

      // fold 1 material thickness adjustment
      p.position.z -= HALF_MT - CLIPPING_FIX_OFFSET;

      // fold 2
      p.position.y += dim.width / 2;
      p.position.x += dim.width / 2 + box.depth - HALF_MT;
      p.rotation.y = -Math.PI / 2;

      // fold 2 material thickness adjustment
      p.position.x += HALF_MT - CLIPPING_FIX_OFFSET/2;
      p.position.z -= 2*CLIPPING_FIX_OFFSET;
      p.position.y += HALF_MT + 2*MT - CLIPPING_FIX_OFFSET;
    }

    if(p.panelName.includes('insideFrontWall')) {
      p.rotation.x = Math.PI / 2
      p.position.z += dim.depth / 2 + HALF_MT + CLIPPING_FIX_OFFSET + box.depth - MT + 5*MT;
      p.position.y -= dim.depth / 2 - HALF_MT + CLIPPING_FIX_OFFSET + MT;
    }

    if(p.panelName.includes('overlap')) {
      p.position.y -= box.depth - 2*MT + 2*CLIPPING_FIX_OFFSET
      p.position.z += box.depth - 2*MT + dim.depth + dim.depth / 2 - MT / 2
      p.rotation.x = Math.PI
    }

    if(p.panelName.includes('outsideFrontWall')) {
      p.position.z += dim.depth / 2 + HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.y -= dim.depth / 2 - HALF_MT + CLIPPING_FIX_OFFSET;
      p.rotation.x = -Math.PI / 2;      
    }

    if (p.panelName.includes('frontRightFlap')) {

      // fold 1
      p.position.z += dim.depth / 2 + MT;
      p.position.y -= dim.depth / 2;
      p.rotation.x = -Math.PI / 2;

      // fold 1 material thickness adjustment
      p.position.z -= HALF_MT - CLIPPING_FIX_OFFSET;

      // fold 2
      p.position.y += dim.width / 2;
      p.position.x -= dim.width / 2 + box.depth + HALF_MT;
      p.rotation.y = Math.PI / 2;

      // fold 2 material thickness adjustment
      p.position.x += HALF_MT - CLIPPING_FIX_OFFSET/2;
      p.position.z -= 2*CLIPPING_FIX_OFFSET;
      p.position.y += HALF_MT + 2*MT - CLIPPING_FIX_OFFSET;
    }

    if(p.panelName.includes('leftOutsideWall')) {
      p.position.x += dim.width / 2 + MT - HALF_MT - CLIPPING_FIX_OFFSET
      p.position.y -= dim.width / 2 - MT + HALF_MT + CLIPPING_FIX_OFFSET
      p.rotation.z = Math.PI / 2
    }

    if(p.panelName.includes('rightOutsideWall')) {
      p.position.x -= dim.width / 2 + MT - HALF_MT - CLIPPING_FIX_OFFSET
      p.position.y -= dim.width / 2 - MT + HALF_MT + CLIPPING_FIX_OFFSET
      p.rotation.z = -Math.PI / 2
    }

    if(p.panelName.includes('backLeftFlap')) {
      // fold 1
      p.position.x += dim.depth / 2 - 2*MT - HALF_MT;
      p.position.y -= dim.depth / 2 - 3*MT - HALF_MT;
      p.rotation.z = Math.PI / 2

      // fold 2
      p.position.z -= dim.width / 2 + 2*MT + HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.x += dim.width / 2 + 2*MT - HALF_MT;
      p.rotation.y = -Math.PI / 2
    }

    if(p.panelName.includes('backWall')) {
      p.position.z -= dim.depth / 2 + MT - HALF_MT - CLIPPING_FIX_OFFSET
      p.position.y -= dim.depth / 2 - MT + HALF_MT + CLIPPING_FIX_OFFSET
      p.rotation.x = Math.PI / 2
    }

    if(p.panelName.includes('backRightFlap')) {
      // fold 1
      p.position.x -= dim.depth / 2 - 2*MT - HALF_MT;
      p.position.y -= dim.depth / 2 - 3*MT - HALF_MT;
      p.rotation.z = -Math.PI / 2

      // fold 2
      p.position.z -= dim.width / 2 + 2*MT + HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.x -= dim.width / 2 + 2*MT - HALF_MT;
      p.rotation.y = Math.PI / 2      
    }


    if(p.panelName.includes('leftClosingFlap')) {
      // // fold 1
      p.position.x += dim.width / 2 + HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.y -= dim.width / 2 - HALF_MT + CLIPPING_FIX_OFFSET;
      p.rotation.z = Math.PI / 2;

      // // fold 2
      p.rotation.x = Math.PI;
      p.position.z -= box.width + 3*MT - 2*MT + box.depth + 3*MT
    }

    if(p.panelName.includes('top')) {
      p.position.y -= box.depth - 2*MT + 3*CLIPPING_FIX_OFFSET
      p.position.z -= box.depth - MT + dim.depth
      p.rotation.x = -Math.PI
    }

    if(p.panelName.includes('rightClosingFlap')) {
      // // fold 1
      p.position.x -= dim.width / 2 + HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.y -= dim.width / 2 - HALF_MT + CLIPPING_FIX_OFFSET;
      p.rotation.z = -Math.PI / 2;

      // fold 2
      p.rotation.x = Math.PI;
      p.position.z -= box.width + 3*MT - 2*MT + box.depth + 3*MT
    }

    if(p.panelName.includes('frontClosingFlap')) {
      p.position.z = HALF_MT + box.depth - MT + 5*MT + box.depth + MT + HALF_MT - CLIPPING_FIX_OFFSET;
      p.position.y -= dim.depth / 2 - HALF_MT + CLIPPING_FIX_OFFSET;
      p.rotation.x = -Math.PI / 2
    }


  });
}

function newFoldPanels(box, group) {

  // Get Box Width, Length, Depth
  const {
    width: W,
    length: L,
    depth: D
  } = box;

  // Get Box Guide
  const {
    x: guideX,
    y: guideY
  } = box.getGuidePoint()

  const boxGuideObj = new THREE.Object3D();
  boxGuideObj.position.set(guideX, 0, guideY);
  group.add(boxGuideObj);

  // Visualize Guide Point
  const cubeGeo = new THREE.SphereGeometry(0.5, 4, 2);
  const cubeMat = new THREE.MeshStandardMaterial({
    wireframe: true,
    color: 0x0000FF,
  })
  const cubeMesh = new THREE.Mesh(cubeGeo, cubeMat);
  boxGuideObj.add(cubeMesh);

  let dim, bb, center;
  let pointNorth, pointSouth, pointWest, pointEast;

  // central panel
  const pBottom = group.children.find(c => c?.panelName.includes('outsideBottom'));
  dim = getObjectDimensions(pBottom)
  bb = new THREE.Box3().setFromObject(pBottom);
  center = new THREE.Vector3();
  bb.getCenter(center);

  pointWest = new THREE.Vector3();
  pointWest.x -= dim.width / 2;
  putDebugCube(pBottom, pointWest)

  pointEast = new THREE.Vector3();
  pointEast.x += dim.width / 2;
  putDebugCube(pBottom, pointEast)

  pointNorth = new THREE.Vector3();
  pointNorth.z -= dim.depth / 2 - MT;
  putDebugCube(pBottom, pointNorth)

  pointSouth = new THREE.Vector3();
  pointSouth.z += dim.depth / 2 - MT;
  putDebugCube(pBottom, pointSouth)

  // front panel
  const pFront = group.children.find(c => c?.panelName.includes('FrontWall'));
  dim = getObjectDimensions(pFront)
  bb = new THREE.Box3().setFromObject(pFront);
  center = new THREE.Vector3();
  bb.getCenter(center);

  pointWest = new THREE.Vector3();
  pointWest.x -= dim.width / 2;
  putDebugCube(pFront, pointWest)

  pointEast = new THREE.Vector3();
  pointEast.x += dim.width / 2;
  putDebugCube(pFront, pointEast)



}

function initLidAnimation(group, scene) {
  const tl = gsap.timeline({ paused: true });

  const lidPivotPoint = new THREE.Object3D();
  // const closingFlapPivotPoint = new THREE.Object3D();

  const top = group.children.find(c => c?.panelName.includes('top'));
  const leftClosingFlap = group.children.find(c => c?.panelName.includes('leftClosingFlap'));
  const rightClosingFlap = group.children.find(c => c?.panelName.includes('rightClosingFlap'));

  const topDim = getObjectDimensions(top)

  const frontClosingFlap = group.children.find(c => c?.panelName.includes('frontClosingFlap'));

  // const frontClosingFlapDim = getObjectDimensions(frontClosingFlap)

  // LID PREPARATION
  // ------------------------------
  lidPivotPoint.closedLidRotation = 0;
  lidPivotPoint.openLidRotation = -(Math.PI / 4) * 3;

  const originalTopPosition = top.position.clone();
  lidPivotPoint.position.copy(originalTopPosition);
  lidPivotPoint.position.z += topDim.depth / 2

  addObjectToPivotPointAndAdjustPosition(lidPivotPoint, top);
  addObjectToPivotPointAndAdjustPosition(lidPivotPoint, leftClosingFlap);
  addObjectToPivotPointAndAdjustPosition(lidPivotPoint, frontClosingFlap);
  addObjectToPivotPointAndAdjustPosition(lidPivotPoint, rightClosingFlap);
  // ------------------------------

  // FRONT CLOSING FLAP PREPARATION
  // ------------------------------
  // closingFlapPivotPoint.closedLidRotation = 0;
  // closingFlapPivotPoint.openLidRotation = 0;

  // const originalFrontClosingFlapPosition = frontClosingFlap.position.clone();
  // closingFlapPivotPoint.position.copy(originalFrontClosingFlapPosition);
  // closingFlapPivotPoint.position.y -= frontClosingFlapDim.height / 2

  // addObjectToPivotPointAndAdjustPosition(closingFlapPivotPoint, frontClosingFlap);

  // addObjectToPivotPointAndAdjustPosition(lidPivotPoint, closingFlapPivotPoint);
  // ------------------------------


  group.add(lidPivotPoint);

  tl.to(lidPivotPoint.rotation, {x: lidPivotPoint.openLidRotation, duration: 1, ease: 'power1.inOut'})
  // .to(closingFlapPivotPoint.rotation, {x: closingFlapPivotPoint.openLidRotation, duration: 0.5, ease: 'power1.in'}, 0.3)

  return tl
}

function addObjectToPivotPointAndAdjustPosition(pivotPoint, panel) {
  const originalPanelPosition = panel.position.clone();

  pivotPoint.add(panel);

  const newPanelPosition = originalPanelPosition.sub(pivotPoint.position)
  panel.position.copy(newPanelPosition);
}

export default function usePizzaBoxModel(box, group, lidTimeline, scene) {

  // fold panels into a box
  foldPanels(box, group);
  // newFoldPanels(box, group);

  setPanelMaterials(group);

  lidTimeline.value = initLidAnimation(group, scene);
  
  scene.add(group);
}
